export interface ApiErrorPayload {
  message?: string | string[];
  statusCode?: number;
  error?: string;
}

const DEFAULT_ERROR_MESSAGE = "Something went wrong";

export const getApiErrorMessage = (
  error: unknown,
  fallback: string = DEFAULT_ERROR_MESSAGE
): string => {
  if (!error) return fallback;

  if (typeof error === "string") return error;

  if (error instanceof Error) {
    return error.message || fallback;
  }

  const payload = error as ApiErrorPayload;

  if (Array.isArray(payload.message)) {
    return payload.message.join(", ");
  }

  if (typeof payload.message === "string" && payload.message.length > 0) {
    return payload.message;
  }

  const axiosError = error as {
    response?: { data?: ApiErrorPayload; status?: number };
  };

  if (axiosError.response?.data) {
    return getApiErrorMessage(axiosError.response.data, fallback);
  }

  return fallback;
};

export const normalizeApiError = (error: unknown): ApiErrorPayload => {
  if (error && typeof error === "object" && "message" in error && !("response" in error)) {
    return error as ApiErrorPayload;
  }

  const axiosError = error as {
    message?: string;
    response?: { data?: ApiErrorPayload; status?: number };
  };

  if (axiosError.response?.data) {
    return {
      ...axiosError.response.data,
      statusCode: axiosError.response.status ?? axiosError.response.data.statusCode,
    };
  }

  if (!axiosError.response) {
    return {
      message: axiosError.message || "Network error. Please check your connection.",
      statusCode: 0,
    };
  }

  return {
    message: DEFAULT_ERROR_MESSAGE,
    statusCode: axiosError.response.status,
  };
};
